import { ref, onValue, set, get, child, update, remove, push, query, orderByChild, equalTo, limitToLast } from 'firebase/database';
import { ref as vueRef, reactive, onUnmounted } from 'vue';
import { getFirebaseDatabase } from './firebase/core';

// 获取数据库实例
let database = getFirebaseDatabase();

// 确保数据库已初始化
function ensureDatabase() {
    if (!database) {
        database = getFirebaseDatabase();
        if (!database) {
            console.error('Firebase Database 服务未初始化');
            throw new Error('Firebase Database 服务未初始化，请确保已调用 initFirebase()');
        }
    }
    return database;
}

export function useRealtimeDatabase() {
    // 用于清理监听器的引用数组
    const listeners = reactive<{ path: string; unsubscribe: () => void }[]>([]);

    /**
     * 实时监听数据变化
     * @param path 数据库路径
     * @param callback 数据变化回调函数
     * @returns 返回数据的ref和清除监听的函数
     */
    function listenToData<T>(path: string, callback?: (data: T) => void) {
        const dataRef = vueRef<T | null>(null);
        const loading = vueRef(true);
        const error = vueRef<Error | null>(null);

        try {
            const db = ensureDatabase();
            const dbRef = ref(db, path);
            const unsubscribe = onValue(
                dbRef,
                snapshot => {
                    loading.value = false;
                    const data = snapshot.val();
                    dataRef.value = data;

                    if (callback) {
                        callback(data);
                    }
                },
                err => {
                    loading.value = false;
                    error.value = err;
                    console.error(`监听路径 ${path} 出错:`, err);
                }
            );

            // 保存监听器引用，以便后续清理
            listeners.push({ path, unsubscribe });

            return {
                data: dataRef,
                loading,
                error,
                unsubscribe: () => {
                    unsubscribe();
                    const index = listeners.findIndex(l => l.path === path);
                    if (index !== -1) {
                        listeners.splice(index, 1);
                    }
                },
            };
        } catch (err) {
            loading.value = false;
            error.value = err as Error;
            console.error(`设置监听器失败 ${path}:`, err);
            return {
                data: dataRef,
                loading,
                error,
                unsubscribe: () => {},
            };
        }
    }

    /**
     * 一次性读取数据
     * @param path 数据库路径
     * @returns Promise解析为数据值
     */
    async function readData<T>(path: string): Promise<T | null> {
        try {
            const db = ensureDatabase();
            const dbRef = ref(db, path);
            const snapshot = await get(dbRef);

            if (snapshot.exists()) {
                return snapshot.val();
            } else {
                console.log(`路径 ${path} 下没有数据`);
                return null;
            }
        } catch (error) {
            console.error(`读取路径 ${path} 出错:`, error);
            throw error;
        }
    }

    /**
     * 写入数据到指定路径
     * @param path 数据库路径
     * @param data 要写入的数据
     * @returns Promise
     */
    async function writeData(path: string, data: any): Promise<void> {
        try {
            const db = ensureDatabase();
            const dbRef = ref(db, path);
            await set(dbRef, data);
            console.log(`数据成功写入到路径 ${path}`);
        } catch (error) {
            console.error(`写入路径 ${path} 出错:`, error);
            throw error;
        }
    }

    /**
     * 更新数据（部分更新）
     * @param path 数据库路径
     * @param updates 要更新的数据字段
     * @returns Promise
     */
    async function updateData(path: string, updates: Record<string, any>): Promise<void> {
        try {
            const db = ensureDatabase();
            const dbRef = ref(db, path);
            await update(dbRef, updates);
            console.log(`数据成功更新路径 ${path}`);
        } catch (error) {
            console.error(`更新路径 ${path} 出错:`, error);
            throw error;
        }
    }

    /**
     * 生成新的唯一键并添加数据
     * @param path 数据库路径
     * @param data 要添加的数据
     * @returns 新数据的键值
     */
    async function pushData(path: string, data: any): Promise<string> {
        try {
            const db = ensureDatabase();
            const dbRef = ref(db, path);
            const newRef = push(dbRef);
            await set(newRef, data);
            console.log(`数据成功推送到路径 ${path} 生成的键值: ${newRef.key}`);
            return newRef.key || '';
        } catch (error) {
            console.error(`推送数据到路径 ${path} 出错:`, error);
            throw error;
        }
    }

    /**
     * 删除指定路径的数据
     * @param path 数据库路径
     * @returns Promise
     */
    async function deleteData(path: string): Promise<void> {
        try {
            const db = ensureDatabase();
            const dbRef = ref(db, path);
            await remove(dbRef);
            console.log(`数据成功从路径 ${path} 删除`);
        } catch (error) {
            console.error(`删除路径 ${path} 出错:`, error);
            throw error;
        }
    }

    /**
     * 查询数据（按子节点值）
     * @param path 数据库路径
     * @param childKey 子节点键名
     * @param value 查询的值
     * @param limit 限制结果数量
     * @returns Promise解析为数据对象
     */
    async function queryByChild<T>(path: string, childKey: string, value: string | number | boolean, limit?: number): Promise<Record<string, T>> {
        try {
            const db = ensureDatabase();
            let queryRef = query(ref(db, path), orderByChild(childKey), equalTo(value));

            if (limit) {
                queryRef = query(queryRef, limitToLast(limit));
            }

            const snapshot = await get(queryRef);

            if (snapshot.exists()) {
                return snapshot.val();
            } else {
                console.log(`查询路径 ${path} 未找到匹配 ${childKey}=${value} 的数据`);
                return {};
            }
        } catch (error) {
            console.error(`查询路径 ${path} 出错:`, error);
            throw error;
        }
    }

    // 组件卸载时清除所有监听器
    onUnmounted(() => {
        listeners.forEach(listener => {
            listener.unsubscribe();
        });
        listeners.splice(0, listeners.length);
    });

    return {
        listenToData,
        readData,
        writeData,
        updateData,
        pushData,
        deleteData,
        queryByChild,
        database,
    };
}
